import * as React from 'react';
import { Dispatch } from 'redux';
import { connect, ConnectedComponent } from 'react-redux';
import { push } from 'connected-react-router';
import { Route, Switch } from 'react-router-dom';
import { withLocalize } from 'react-localize-redux';
import { renderToStaticMarkup } from 'react-dom/server';

import AppState from '@webapp/store/state';
import { UIState } from '@webapp/store/ui/state';
import { ListCountries } from '@webapp/store/categories/action-creators';

import { AnimatePresence } from 'framer-motion';

import TermConditions from '@webapp/pages/merch/pricing-owners/term-and-conditions';
import Catalog from '@webapp/pages/catalog';
import MerchPage from '@webapp/pages/merch';
import PaymentPage from '@webapp/pages/payment';
import PricingPage from '@webapp/pages/pricing';
import ReleasePage from '@webapp/pages/release';
import EarningsPage from '@webapp/pages/earnings';
import LoginPage from '@webapp/pages/login/index';
import NotFoundPage from '@webapp/pages/not-found';
import NewDashboardGeneralPage from '@webapp/pages/dashboard/new-dashboard';
import ArtistSitePage from '@webapp/pages/tracpage';
import OnboardingPage from '@webapp/pages/onboarding';
import MusicVideoPage from '@webapp/pages/music-video';
import UserProfilePage from '@webapp/pages/user-profile';
import RegistrationPage from '@webapp/pages/registration';
import ArtistProfilePage from '@webapp/pages/artist-profile';
import PrivacyPolicyPage from '@webapp/pages/privacy-policy';
import ArtistSelectorPage from '@webapp/pages/artist-selector';
import PasswordPromptPage from '@webapp/pages/password-prompt';
import TermsOfServicePage from '@webapp/pages/terms-of-service';
import TwoFactorPromptPage from '@webapp/pages/two-factor-prompt';
import UnsubscribePage from '@webapp/pages/artist-profile/unsubscribe';
import RepairReleasePage from '@webapp/pages/catalog/repair-release';
import PlanDowngradePage from '@webapp/pages/pricing/plan-downgrade';
import DowngradeLabelPage from '@webapp/pages/pricing/downgrade-label';
import WaitlistSuccessPage from '@webapp/pages/merch/waitlist-success';
import UpgradeReleasePage from '@webapp/pages/catalog/upgrade-release';
import DowngradeSuccessPage from '@webapp/pages/pricing/downgrade-success';
import ReleaseSubscriptionPlan from '@webapp/pages/release-subsciption-plan';
import CreateArtistPage from '@webapp/pages/artist-profile/artist-create/index';
import AlphaPilotUserAgreementPage from '@webapp/pages/alpha-pilot-user-agreement';

import Header from '@components/layout/header/index';
import Container from '@components/layout/container/index';
import ConsentBanner from '@components/layout/consent-banner';
import SnackbarStatus from '@components/basics/snackbar/snackbar-status';
import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';
import SnackbarNotification from '@components/basics/snackbar/snackbar-notification';

import NavbarPageCtrl from '@webapp/controllers/navbar/navbar-pages';
import ReleaseSidebarCtrl from '@webapp/controllers/release/release-sidebar';
import NavbarActionsCtrl from '@webapp/controllers/navbar/navbar-actions/index';

import MerchEditPage from './pages/merch-edit';
import MerchPubLishPageSuccess from './pages/merch/merch-subscription-success/publish-merch';
import MerchCreateSuccess from './pages/merch/merch-create-success';
import CorrectArtistProfileInfoPage from './pages/artist-profile/correct-artist-profile';
import TakedownRelease from './pages/catalog/takedown-release';
import ChangeSplit from './pages/user-profile/profile/change-split';
import distributionAgreement from './pages/distribution-agreement';
import MerchOrderPaymentPage from './pages/merch-order/payment';
import MerchOrderConfirmationPage from './pages/merch-order/confirmation';
import PublishedBg from './pages/release/published/published-bg';
import EarningsDetailsPage from './pages/earnings/details';
import NewAnalyticsPage from './pages/analytics/new-analytics';
import AdminSignIn from './pages/admin-signIn';
import AccountConfirmationPage from './pages/account-confirmation';
import ConfirmationPage from './pages/confirmation';
import CommunityLandingPage from './pages/community-landing';
import PollPage from './pages/poll';
import ArtistRestore from './pages/artist-restore';
import LabelProfilePage from './pages/label';
import ProfilePage from './pages/profile';

declare type RouteType = {
  path?: string;
  exact?: boolean;
  component:
    | string
    | React.FunctionComponent<{}>
    | React.ComponentClass<{}, any>
    | ConnectedComponent<any, any>;
};

const ROUTES: RouteType[] = [
  { exact: true, path: '/', component: () => <div></div> },
  { path: '/register', component: RegistrationPage },
  { path: '/login', component: LoginPage },
  { path: '/admin-login/:challenge', component: AdminSignIn },
  { path: '/onboarding', component: OnboardingPage },
  { exact: true, path: '/plans/:clientId?/:id?', component: PricingPage },
  {
    exact: true,
    path: '/plans/:clientId/:id/downgrade',
    component: PlanDowngradePage,
  },
  {
    exact: true,
    path: '/plans/:clientId/:id/downgrade-success',
    component: DowngradeSuccessPage,
  },
  {
    exact: true,
    path: '/plans/:clientId/:id/downgrade-label',
    component: DowngradeLabelPage,
  },
  { path: '/dashboard', component: NewDashboardGeneralPage },
  { path: '/analytics', component: NewAnalyticsPage },
  { path: '/user', component: UserProfilePage },
  { path: '/label', component: LabelProfilePage },
  { exact: true, path: '/artist/new', component: CreateArtistPage },
  { path: '/artist/page', component: ArtistSitePage },
  { path: '/artist', component: ArtistProfilePage },
  {
    path: '/correct-profile/:releaseId',
    component: CorrectArtistProfileInfoPage,
  },
  {
    path: '/takedown-release/:releaseId/:artistId',
    component: TakedownRelease,
  },
  { path: '/artist-selector', component: ArtistSelectorPage },
  { path: '/release', component: ReleasePage },
  { path: '/release-subscription-plan', component: ReleaseSubscriptionPlan },
  { path: '/merch/edit/:merchId', component: MerchEditPage },
  {
    path: '/merch/create/success',
    component: MerchCreateSuccess,
    exact: true,
  },
  { path: '/merch', component: MerchPage },
  { path: '/music-video', component: MusicVideoPage },
  { exact: true, path: '/merch', component: MerchPage },
  {
    exact: true,
    path: '/merch/waitlist-success',
    component: WaitlistSuccessPage,
  },
  { path: '/terms-of-service', component: TermsOfServicePage },
  { path: '/distribution-agreement', component: distributionAgreement },
  { path: '/privacy-policy', component: PrivacyPolicyPage },
  {
    path: '/upgrade-release/:artistId/:releaseId',
    component: UpgradeReleasePage,
  },
  { path: '/catalog/repair-release', component: RepairReleasePage },
  {
    path: '/alpha-pilot-user-agreement',
    component: AlphaPilotUserAgreementPage,
  },
  {
    exact: true,
    path: '/payment/:clientId?/:id?/:type?',
    component: PaymentPage,
  },
  { path: '/catalog/:type?/:productId?', component: Catalog },
  { path: '/confirmation', component: ConfirmationPage },
  { path: '/account-confirmation', component: AccountConfirmationPage },
  {
    exact: true,
    path: '/earnings',
    component: EarningsPage,
  },
  {
    exact: true,
    path: '/earnings/:section/:reportDate',
    component: EarningsDetailsPage,
  },
  {
    exact: true,
    path: '/profile/:page',
    component: ProfilePage,
  },
  {
    exact: true,
    path: '/user-profile/change-split/:assetType/:releaseId/:trackId/:songName/:type/:lock',
    component: ChangeSplit,
  },
  {
    exact: true,
    path: '/merch-payment/published-success',
    component: MerchPubLishPageSuccess,
  },
  {
    exact: true,
    path: '/merch-agreement',
    component: TermConditions,
  },
  {
    exact: true,
    path: '/merch-order/payment/',
    component: MerchOrderPaymentPage,
  },
  {
    exact: true,
    path: '/merch-order/confirmation/',
    component: MerchOrderConfirmationPage,
  },
  {
    exact: true,
    path: '/community',
    component: CommunityLandingPage,
  },
  {
    exact: true,
    path: '/unsubscribe',
    component: UnsubscribePage,
  },
  {
    exact: true,
    path: '/unsubscribe/poll',
    component: PollPage,
  },
  {
    exact: true,
    path: '/2fa/:type',
    component: TwoFactorPromptPage,
  },
  {
    exact: true,
    path: '/password-2fa',
    component: PasswordPromptPage,
  },
  {
    exact: true,
    path: '/close-account',
    component: PollPage,
  },
  {
    path: '/restore/artist/:id?',
    component: ArtistRestore,
  },
  { component: NotFoundPage },
];

const mapStateToProps = (state: AppState) => ({
  loggedIn: !!state.auth.user,
  isMobile: state.ui.mobile,
  isArtistPreview: state.tracpage.artistPreview,
  isOnboarding:
    state.router.location.pathname.startsWith('/onboarding') ||
    state.router.location.pathname.startsWith('/plans') ||
    state.router.location.pathname.startsWith('/artist-selector') ||
    state.router.location.pathname.endsWith('/unsubscribe/merch') ||
    state.router.location.pathname.startsWith('/close-account') ||
    state.router.location.pathname.startsWith(
      '/artist/subscriptions/invoices'
    ) ||
    state.router.location.pathname.startsWith('/payment'),
  isEditingRelease: state.router.location.pathname.startsWith('/release'),
  isEditingMerch:
    [
      '/merch/select-product',
      '/merch/color-size',
      '/merch/design',
      '/merch/pricing-owners',
    ].includes(state.router.location.pathname) ||
    state.router.location.pathname.startsWith('/merch/edit/'),
  isUpgradingRelease:
    state.router.location.pathname.endsWith('/upgrade-release'),
  isArtistSite: state.router.location.pathname.startsWith('/artist'),
  isMusicVideo: state.router.location.pathname.startsWith('/music-video'),
  isMerch: state.router.location.pathname.startsWith('/merch'),
  isPublished:
    state.router.location.pathname.endsWith('/published') ||
    state.router.location.pathname.endsWith('/merch-payment/published-success'),
  onlyName:
    state.router.location.pathname.startsWith('/2fa') ||
    state.router.location.pathname.startsWith('/password-2fa') ||
    state.router.location.pathname.startsWith('/user/banking/history') ||
    state.router.location.pathname.startsWith('/user/banking/new-account') ||
    state.router.location.pathname.startsWith('/user/banking/success') ||
    state.router.location.pathname.startsWith('/restore/artist'),
  isTermsOfService:
    state.router.location.pathname.startsWith('/terms-of-service'),
  isPrivacyPolicy: state.router.location.pathname.startsWith('/privacy-policy'),
  notifications: state.ui.snackbar,
  notificationsStatus: state.ui.snackbarStatus,
  noHeader:
    state.router.location.pathname.startsWith('/community') ||
    state.router.location.pathname.startsWith('/profile'),
  openedMenu: state.ui.menuOpened,
  disableContainerMaxWidth:
    state.router.location.pathname.startsWith('/profile'),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  listCountries: () => dispatch(new ListCountries()),
  goTo: (path: string) => dispatch(push(path)),
});

export class MainRouter extends React.Component<any> {
  public state = {
    dismissedNotifications: [] as string[],
  };

  private dismiss(key: string) {
    this.setState({
      dismissedNotifications: this.state.dismissedNotifications.concat([key]),
    });
  }

  constructor(props: any) {
    super(props);

    this.props.initialize({
      languages: [
        { name: 'English', code: 'en' },
        { name: 'Español', code: 'es' },
      ],
      options: {
        renderToStaticMarkup,
        renderInnerHtml: true,
        defaultLanguage: 'en',
        onMissingTranslation: () => '',
      },
    });

    fetch(`${process.env.REACT_APP_BASENAME}data/lang/en.json`)
      .then((res) => res.json())
      .then((t) => this.props.addTranslationForLanguage(t, 'en'));
    fetch(`${process.env.REACT_APP_BASENAME}data/lang/es.json`)
      .then((res) => res.json())
      .then((t) => this.props.addTranslationForLanguage(t, 'es'));

    this.props.listCountries();
  }

  render() {
    return (
      <>
        {this.props.isPublished && <PublishedBg />}
        {(this.props.isMobile &&
          this.props.isArtistSite &&
          this.props.isArtistPreview) ||
        this.props.noHeader ? null : (
          <Header
            distributeContent={this.props.loggedIn && !this.props.onlyName}
            actions={
              this.props.loggedIn &&
              !this.props.isOnboarding &&
              !this.props.isTermsOfService &&
              !this.props.isPrivacyPolicy ? (
                <NavbarActionsCtrl />
              ) : undefined
            }
            whiteBackground={
              !!(this.props.isTermsOfService || this.props.isPrivacyPolicy)
            }
            mobile={this.props.isMobile}
            mobileMenu={
              this.props.isEditingRelease ? <ReleaseSidebarCtrl /> : null
            }
          >
            {this.props.isOnboarding ||
            this.props.isEditingRelease ||
            this.props.isEditingMerch ||
            this.props.onlyName ||
            this.props.isUpgradingRelease ||
            this.props.isMusicVideo ||
            this.props.isTermsOfService ||
            this.props.isPrivacyPolicy ? null : this.props.loggedIn ? (
              <NavbarPageCtrl />
            ) : null}
          </Header>
        )}
        <Container
          disabledMaxWidth={this.props.disableContainerMaxWidth}
          // This is added to stop the app from scrolling behind the opened menu in mobile
          className={
            this.props.openedMenu && this.props.isMobile ? 'fixed-height' : ''
          }
        >
          <AnimatePresence exitBeforeEnter>
            <Switch>
              {ROUTES.map((route, index) => (
                <Route
                  path={route.path}
                  exact={route.exact}
                  key={index}
                  render={(props: any) =>
                    React.createElement(route.component, {
                      ...props,
                    })
                  }
                />
              ))}
            </Switch>
          </AnimatePresence>
        </Container>

        {Object.values(this.props.notifications).filter((p) => !!p).length ? (
          <SnackbarContainer>
            {Object.keys(this.props.notifications as UIState['snackbar'])
              .filter((key) => !this.state.dismissedNotifications.includes(key))
              .map((key) => {
                const notification = this.props.notifications[key];

                if (!notification) {
                  return null;
                }

                return (
                  <SnackbarNotification
                    key={key}
                    title={notification.title}
                    text={notification.text}
                    btnText={notification.action}
                    hasDismiss={!notification.hideDismiss}
                    onAction={() => this.dismiss(key)}
                    onClick={() => {
                      this.props.goTo(notification.to);
                      this.dismiss(key);
                    }}
                  />
                );
              })}
          </SnackbarContainer>
        ) : null}

        {Object.values(this.props.notificationsStatus).filter((p) => !!p)
          .length ? (
          <SnackbarContainer>
            {Object.keys(
              this.props.notificationsStatus as UIState['snackbarStatus']
            ).map((key) => {
              const notification = this.props.notificationsStatus[key];

              if (!notification) {
                return null;
              }

              return (
                <SnackbarStatus
                  key={key}
                  title={notification.title}
                  status={notification.status}
                  onClose={() => this.dismiss(key)}
                  hideOnClose={notification.hideOnClose}
                />
              );
            })}
          </SnackbarContainer>
        ) : null}
        <ConsentBanner />
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withLocalize(MainRouter));
